import { WebRTCConfig } from "../types";

class WebRTCService {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private isAudioMuted = false;

  private config: WebRTCConfig = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
    ],
  };

  // Event callbacks
  private onIceCandidateCallback:
    | ((candidate: RTCIceCandidate) => void)
    | null = null;
  private onRemoteStreamCallback: ((stream: MediaStream) => void) | null = null;
  private onConnectionStateChangeCallback:
    | ((state: RTCPeerConnectionState) => void)
    | null = null;

  constructor(config?: Partial<WebRTCConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
  }

  async getLocalAudioStream(): Promise<MediaStream> {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      // Set local audio element
      const localAudio = document.getElementById(
        "local-audio"
      ) as HTMLAudioElement;
      if (localAudio) {
        localAudio.srcObject = this.localStream;
      }

      console.log("Local audio stream obtained successfully");
      return this.localStream;
    } catch (error) {
      console.error("Failed to get local audio stream:", error);
      throw new Error(
        "Cannot access microphone, please check permission settings"
      );
    }
  }

  // Initialize PeerConnection
  async initializePeerConnection(): Promise<void> {
    if (this.peerConnection) {
      this.peerConnection.close();
    }

    this.peerConnection = new RTCPeerConnection(this.config);

    // Ensure local audio stream is available
    if (!this.localStream) {
      try {
        await this.getLocalAudioStream();
      } catch (error) {
        console.error(
          "Failed to get local audio stream during initialization:",
          error
        );
        throw error;
      }
    }

    // Add local audio stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        this.peerConnection!.addTrack(track, this.localStream!);
      });
      console.log("Local audio tracks added to peer connection");
    }

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      console.log("Remote audio stream received");
      this.remoteStream = event.streams[0];
      if (this.onRemoteStreamCallback) {
        this.onRemoteStreamCallback(this.remoteStream);
      }
    };

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.onIceCandidateCallback) {
        this.onIceCandidateCallback(event.candidate);
      }
    };

    // Connection state monitoring
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection!.connectionState;
      console.log("WebRTC connection state:", state);
      if (this.onConnectionStateChangeCallback) {
        this.onConnectionStateChangeCallback(state);
      }
    };

    console.log("PeerConnection initialized successfully");
  }

  // Create Offer
  async createOffer(): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("PeerConnection not initialized");
    }

    try {
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      console.log("Offer created successfully");
      return offer;
    } catch (error) {
      console.error("Failed to create offer:", error);
      throw error;
    }
  }

  // Create Answer
  async createAnswer(
    offer: RTCSessionDescriptionInit
  ): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("PeerConnection not initialized");
    }

    try {
      await this.peerConnection.setRemoteDescription(offer);
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      console.log("Create Answer successfully");
      return answer;
    } catch (error) {
      console.error("Failed to create answer:", error);
      throw error;
    }
  }

  // Handle Answer
  async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      throw new Error("PeerConnection not initialized");
    }

    try {
      await this.peerConnection.setRemoteDescription(answer);
      console.log("Handle Answer successfully");
    } catch (error) {
      console.error("Failed to handle answer:", error);
      throw error;
    }
  }

  // Add ICE candidate
  async addIceCandidate(candidate: RTCIceCandidate): Promise<void> {
    if (!this.peerConnection) {
      throw new Error("PeerConnection not initialized");
    }

    try {
      await this.peerConnection.addIceCandidate(candidate);
      console.log("Add ICE candidate successfully");
    } catch (error) {
      console.error("Failed to add ICE candidate:", error);
      throw error;
    }
  }

  // Toggle mute status
  toggleMute(): boolean {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        this.isAudioMuted = !audioTrack.enabled;
        console.log("Audio mute status:", this.isAudioMuted);
        return this.isAudioMuted;
      }
    }
    return false;
  }

  // Get mute status
  isMuted(): boolean {
    return this.isAudioMuted;
  }

  // End call
  endCall(): void {
    // Stop local audio stream
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    // Close PeerConnection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    this.isAudioMuted = false;
    console.log("Call ended");
  }

  // Set event callbacks
  onIceCandidate(callback: (candidate: RTCIceCandidate) => void): void {
    this.onIceCandidateCallback = callback;
  }

  onRemoteStream(callback: (stream: MediaStream) => void): void {
    this.onRemoteStreamCallback = callback;
  }

  onConnectionStateChange(
    callback: (state: RTCPeerConnectionState) => void
  ): void {
    this.onConnectionStateChangeCallback = callback;
  }

  // Get connection state
  getConnectionState(): RTCPeerConnectionState | undefined {
    return this.peerConnection?.connectionState;
  }

  // Get local stream
  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  // Get remote stream
  getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }
}

export default WebRTCService;
